/* eslint-disable no-param-reassign */
// Creates generic data model based off savant's default model
// Looks at the schema to determine what type of chart is being requested and creates the appropriate model
// This helps make the generic model simple for non savant/statsapi requests

// px required
// pz required
// legendType required

import get from 'lodash/get'
import filter from 'lodash/filter'

import TooltipHtml from '../Tooltip/Html/SingleEvent'
import {
    RemoveBadPitchPoints,
    RemoveBadSprayPoints,
} from '../Utilities/RemoveBadPoints'

export default function Convert(schema, savantData) {
    let data = []

    const chartOptions = get(schema, 'charts[0]')

    if (chartOptions.chartType) {
        if (
            chartOptions.chartType === 'pitch' ||
            chartOptions.chartType === 'release' ||
            chartOptions.chartType === 'field' ||
            chartOptions.chartType === 'radial' ||
            chartOptions.chartType === '3d' ||
            chartOptions.chartType === 'movement'
        ) {
            savantData = filterSavantData(chartOptions, savantData)
            data = savantData.map((d) =>
                formatSavantData(chartOptions, d, Boolean(schema.showToolTip))
            )
        } else {
            data = savantData.map((d) => formatSavantData(chartOptions, d))
        }
    }

    return data
}

// Remove various data points based on chart types
function filterSavantData({ chartType = 'pitch' }, savantData) {
    if (
        chartType === 'pitch' ||
        chartType === 'release' ||
        chartType === 'network'
    ) {
        return RemoveBadPitchPoints(savantData)
    }
    if (chartType === 'movement') {
        const badValues = [undefined, null, 'null', '']
        return filter(
            savantData,
            // These values come from a csv with these named columns
            (d) =>
                !badValues.includes(d.pfx_x) &&
                !badValues.includes(d.pfx_z_gravity)
        )
    }
    if (chartType === 'field' || chartType === 'radial' || chartType === '3d') {
        return RemoveBadSprayPoints(savantData)
    }
    if (chartType === 'radial') {
        return filter(
            savantData,
            (d) =>
                (d.launch_speed && d.launch_angle) ||
                (d.h_launch_speed && d.h_launch_angle) ||
                (d.hit_speed && d.hit_angle)
        )
    }

    return savantData
}

function formatSavantData(
    { legendType = 'pitchName', chartType = 'pitch' },
    d,
    showToolTip = false
) {
    let x = 'plate_x'
    let y = 'plate_z'
    if (chartType === 'field' || chartType === '3d') {
        x = 'hc_x_ft'
        y = 'hc_y_ft'
    }
    if (chartType === 'pitch') {
        x = 'plate_x'
        y = 'plate_z'
    }
    if (chartType === 'movement') {
        x = 'pfx_x'
        y = 'pfx_z_gravity'
    }
    if (chartType === 'release') {
        x = 'x0'
        y = 'z0'
    }

    return {
        x: d[x],
        y: d[y],
        pitchType: d.pitch_type,
        pitchName: d.pitch_name,
        legend: d[legendType] || null,
        paResult: d.events,
        pitchResult: d.description,
        hits: Number(d.is_hit_into_play_basehit),
        battedBallType: d.bb_type,
        ab: Number(d.is_ab),
        pa: Number(d.is_pa),
        bip: Number(d.is_hit_into_play),
        trackedBIP:
            Number(d.launch_speed) > 0 || Number(d.hit_speed) > 0
                ? Number(d.is_hit_into_play)
                : 0,
        trackedSwingLength: Number(d.swing_length) ? 1 : 0,
        trackedBatSpeed: Number(d.sweetspot_speed_mph) ? 1 : 0,
        trackedSwingLengthCompetitive:
            Number(d.swing_length) && Number(d.is_competitive) ? 1 : 0,
        trackedBatSpeedCompetitive:
            Number(d.sweetspot_speed_mph) && Number(d.is_competitive) ? 1 : 0,
        single: Number(d.is_hit_into_play_basehit_single),
        double: Number(d.is_hit_into_play_basehit_double),
        triple: Number(d.is_hit_into_play_basehit_triple),
        hr: Number(d.is_hit_into_play_basehit_home_run),
        so: Number(d.is_strikeout),
        zone: d.zone,
        callName: d.call_name,
        balls: Number(d.pre_ball_count) || Number(d.balls),
        strikes: Number(d.pre_strike_count) || Number(d.strikes),
        releaseSpeed: Number(d.release_speed) || Number(d.start_speed),
        releaseSpinRate: Number(d.release_spin_rate) || 0,
        pfxX: Number(d.pfx_x) || null,
        pfxZGravity: Number(d.pfx_z_gravity) || null,
        launchSpeed: d.is_hit_into_play
            ? Number(d.launch_speed) || Number(d.hit_speed) || 0
            : 0,
        launchAngle: d.is_hit_into_play
            ? Number(d.launch_angle) || Number(d.hit_angle) || 0
            : 0,
        swingLength: Number(d.swing_length) || null,
        batSpeed: Number(d.sweetspot_speed_mph) || null,
        is_swing: Number(d.is_swing) || null,
        is_whiff: Number(d.is_whiff) || null,
        deltaRunExp: Number(d.delta_run_exp),
        wobaNumer: Number(d.fangraphs_woba_numer),
        wobaDenom: Number(d.fangraphs_woba_denom),
        xWobaNumer: Number(d.fangraphs_est_woba_numer),
        xWobaDenom: Number(d.fangraphs_est_woba_denom),
        tooltip: showToolTip ? TooltipHtml(d) : null,
        videoUrl:
            d.game_year >= 2016 && d.play_id
                ? `https://baseballsavant.mlb.com/sporty-videos?playId=${d.play_id}`
                : null,
        raw: d,
    }
}
